import type { Bookmark, ConversationCard } from "./domain";
import type { SummaryEnrichmentJob } from "./repository";

export type OwnerDestinationFilter = "all" | "cards" | "library" | "both";
export type OwnerReviewFilter =
  | "all"
  | "unclassified"
  | "summary"
  | "search_metadata"
  | "destination";
export type OwnerPreparationSort = "newest" | "oldest" | "shuffled";
export type OwnerSourceFilterMode = "any" | "include" | "exclude";

export const UNKNOWN_OWNER_SOURCE = "__unknown__";
export const CONVERSATION_DECK_PREFERENCES_STORAGE_KEY =
  "convoful.conversation-deck-findability.v1";

const conversationDeckPreferenceSchemaVersion = 1;
const allowedOwnerSourceIDs = [
  "instagram",
  "x",
  "whatsapp",
  "web_other",
  UNKNOWN_OWNER_SOURCE,
] as const;
const allowedOwnerSourceIDSet = new Set<string>(allowedOwnerSourceIDs);

const knownOwnerSourceLabels: Readonly<Record<string, string>> = {
  instagram: "Instagram",
  x: "X",
  whatsapp: "WhatsApp",
  web_other: "Web / other",
};

const ownerSourceCategories: Readonly<Record<string, string>> = {
  instagram: "instagram",
  x: "x",
  "x-bookmark-folder": "x",
  whatsapp: "whatsapp",
  web: "web_other",
  other: "web_other",
};

export interface OwnerPreparationFilters {
  query: string;
  audienceID: string;
  destination: OwnerDestinationFilter;
  cluster: string;
  review: OwnerReviewFilter;
  includedSources: ReadonlySet<string>;
  excludedSources: ReadonlySet<string>;
  sort: OwnerPreparationSort;
  shuffleSeed: string;
}

export interface OwnerSourceOption {
  id: string;
  label: string;
  count: number;
}

export interface ConversationDeckPreferences {
  sort: OwnerPreparationSort;
  includedSources: ReadonlySet<string>;
  excludedSources: ReadonlySet<string>;
}

export interface ConversationDeckPreferenceLoadResult {
  preferences: ConversationDeckPreferences;
  recoveredInvalidState: boolean;
}

export interface OwnerPreparationCounts {
  total: number;
  shown: number;
  selected: number;
  unsaved: number;
  pending: number;
  failed: number;
  needsReview: number;
}

export interface OwnerPreparationCountInput {
  bookmarks: readonly Bookmark[];
  shownBookmarks: readonly Bookmark[];
  selectedBookmarkIDs: ReadonlySet<string>;
  unsavedBookmarkIDs: ReadonlySet<string>;
  enrichmentJobs: readonly SummaryEnrichmentJob[];
}

function normalizedText(value: string): string {
  return value
    .normalize("NFKD")
    .replace(/\p{Diacritic}/gu, "")
    .toLocaleLowerCase("en")
    .replace(/[^\p{Letter}\p{Number}]+/gu, " ")
    .trim()
    .replace(/\s+/g, " ");
}

function humanizeCluster(cluster: string): string {
  return cluster
    .trim()
    .replaceAll("-", " ")
    .replace(/\s+/g, " ");
}

export function ownerSourceID(bookmark: Pick<Bookmark, "sourceLabel">): string {
  // The owner-workspace adapter stores the immutable raw `source` token in
  // sourceLabel. Never classify from an editable or audience-visible label.
  const source = bookmark.sourceLabel.trim();
  return Object.hasOwn(ownerSourceCategories, source)
    ? ownerSourceCategories[source]
    : UNKNOWN_OWNER_SOURCE;
}

export function ownerSourceLabel(sourceID: string): string {
  return knownOwnerSourceLabels[sourceID] ?? "Unknown source";
}

export function ownerSourceOptions(
  bookmarks: readonly Bookmark[],
): OwnerSourceOption[] {
  const counts = new Map<string, number>();
  for (const bookmark of bookmarks) {
    const sourceID = ownerSourceID(bookmark);
    counts.set(sourceID, (counts.get(sourceID) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([id, count]) => ({
      id,
      label: ownerSourceLabel(id),
      count,
    }))
    .sort((left, right) => (
      left.label.localeCompare(right.label)
      || left.id.localeCompare(right.id)
    ));
}

export function ownerBookmarkMatchesDestination(
  bookmark: Bookmark,
  audienceID: string,
  destination: OwnerDestinationFilter,
): boolean {
  if (audienceID === "all" && destination === "all") return true;
  return bookmark.audienceAssignments.some((assignment) => {
    if (audienceID !== "all" && assignment.audienceId !== audienceID) {
      return false;
    }
    switch (destination) {
      case "all":
        return assignment.conversationEnabled || assignment.libraryEnabled;
      case "cards":
        return assignment.conversationEnabled;
      case "library":
        return assignment.libraryEnabled;
      case "both":
        return assignment.conversationEnabled && assignment.libraryEnabled;
    }
  });
}

export function ownerBookmarkMatchesReview(
  bookmark: Bookmark,
  review: OwnerReviewFilter,
): boolean {
  switch (review) {
    case "all":
      return true;
    case "unclassified":
      return (
        !bookmark.cluster
        || bookmark.cluster === "unclassified-social-saves"
      );
    case "summary":
      return (
        !bookmark.privateHumanSummary.trim()
        || bookmark.vectorStatus !== "ready"
      );
    case "search_metadata":
      return bookmark.keywords.length === 0 || bookmark.tags.length === 0;
    case "destination":
      return !bookmark.audienceAssignments.some(
        ({ conversationEnabled, libraryEnabled }) => (
          conversationEnabled || libraryEnabled
        ),
      );
  }
}

function sourceIncluded(
  bookmark: Pick<Bookmark, "sourceLabel">,
  includedSources: ReadonlySet<string>,
  excludedSources: ReadonlySet<string>,
): boolean {
  const sourceID = ownerSourceID(bookmark);
  if (excludedSources.has(sourceID)) return false;
  return includedSources.size === 0 || includedSources.has(sourceID);
}

export function defaultConversationDeckPreferences():
ConversationDeckPreferences {
  return {
    sort: "shuffled",
    includedSources: new Set(),
    excludedSources: new Set(),
  };
}

export function conversationDeckSourceOptions(
  cards: readonly ConversationCard[],
): OwnerSourceOption[] {
  const counts = new Map<string, number>();
  for (const card of cards) {
    const sourceID = ownerSourceID(card);
    counts.set(sourceID, (counts.get(sourceID) ?? 0) + 1);
  }
  return allowedOwnerSourceIDs.map((id) => ({
    id,
    label: ownerSourceLabel(id),
    count: counts.get(id) ?? 0,
  }));
}

export function presentConversationDeck(
  sessionOrderedCards: readonly ConversationCard[],
  preferences: ConversationDeckPreferences,
): ConversationCard[] {
  const cards = sessionOrderedCards.filter((card) => sourceIncluded(
    card,
    preferences.includedSources,
    preferences.excludedSources,
  ));
  if (preferences.sort === "shuffled") return [...cards];
  return cards.sort((left, right) => {
    const dateOrder = preferences.sort === "newest"
      ? right.createdAt.localeCompare(left.createdAt)
      : left.createdAt.localeCompare(right.createdAt);
    return dateOrder || left.bookmarkID.localeCompare(right.bookmarkID);
  });
}

function orderedOwnerSourceIDs(values: ReadonlySet<string>): string[] {
  return allowedOwnerSourceIDs.filter((id) => values.has(id));
}

export function serializeConversationDeckPreferences(
  preferences: ConversationDeckPreferences,
): string {
  return JSON.stringify({
    schemaVersion: conversationDeckPreferenceSchemaVersion,
    sort: preferences.sort,
    includedSourceIDs: orderedOwnerSourceIDs(preferences.includedSources),
    excludedSourceIDs: orderedOwnerSourceIDs(preferences.excludedSources),
  });
}

function parseOwnerSourceIDs(value: unknown): string[] | null {
  if (!Array.isArray(value) || value.length > allowedOwnerSourceIDs.length) {
    return null;
  }
  const sourceIDs = value.filter((sourceID): sourceID is string => (
    typeof sourceID === "string"
  ));
  if (
    sourceIDs.length !== value.length
    || new Set(sourceIDs).size !== sourceIDs.length
    || sourceIDs.some((sourceID) => !allowedOwnerSourceIDSet.has(sourceID))
  ) return null;
  return sourceIDs;
}

export function loadConversationDeckPreferences(
  stored: string | null,
): ConversationDeckPreferenceLoadResult {
  if (stored === null) {
    return {
      preferences: defaultConversationDeckPreferences(),
      recoveredInvalidState: false,
    };
  }
  try {
    const value = JSON.parse(stored) as unknown;
    if (typeof value !== "object" || value === null || Array.isArray(value)) {
      throw new Error("Conversation deck preferences must be an object.");
    }
    const record = value as Record<string, unknown>;
    const exactKeys = [
      "excludedSourceIDs",
      "includedSourceIDs",
      "schemaVersion",
      "sort",
    ];
    if (
      Object.keys(record).sort().join("\u0000")
      !== exactKeys.join("\u0000")
      || record.schemaVersion !== conversationDeckPreferenceSchemaVersion
      || !["newest", "oldest", "shuffled"].includes(String(record.sort))
    ) {
      throw new Error("Conversation deck preferences are incompatible.");
    }
    const includedSources = parseOwnerSourceIDs(record.includedSourceIDs);
    const excludedSources = parseOwnerSourceIDs(record.excludedSourceIDs);
    if (!includedSources || !excludedSources) {
      throw new Error("Conversation deck sources are invalid.");
    }
    return {
      preferences: {
        sort: record.sort as OwnerPreparationSort,
        includedSources: new Set(includedSources),
        excludedSources: new Set(excludedSources),
      },
      recoveredInvalidState: false,
    };
  } catch {
    return {
      preferences: defaultConversationDeckPreferences(),
      recoveredInvalidState: true,
    };
  }
}

export function conversationDeckPreferenceSummary(
  preferences: ConversationDeckPreferences,
  resultCount: number,
): string {
  const included = orderedOwnerSourceIDs(preferences.includedSources)
    .map(ownerSourceLabel);
  const excluded = orderedOwnerSourceIDs(preferences.excludedSources)
    .map(ownerSourceLabel);
  const sourceSummary = included.length
    ? `Sources: ${included.join(" + ")}${
      excluded.length ? ` except ${excluded.join(" + ")}` : ""
    }`
    : excluded.length
      ? `All except ${excluded.join(" + ")}`
      : "All sources";
  const sortLabel = preferences.sort === "newest"
    ? "Newest first"
    : preferences.sort === "oldest"
      ? "Oldest first"
      : "Shuffled session order";
  return `${sortLabel} · ${sourceSummary} · ${resultCount} ${
    resultCount === 1 ? "playable card" : "playable cards"
  }`;
}

function stableHash(value: string): number {
  let hash = 2_166_136_261;
  for (const character of value) {
    hash ^= character.codePointAt(0) ?? 0;
    hash = Math.imul(hash, 16_777_619);
  }
  return hash >>> 0;
}

function compareBookmarkIdentity(left: Bookmark, right: Bookmark): number {
  return left.title.localeCompare(right.title) || left.id.localeCompare(right.id);
}

export function ownerPreparationBookmarks(
  bookmarks: readonly Bookmark[],
  filters: OwnerPreparationFilters,
): Bookmark[] {
  const queryTerms = normalizedText(filters.query).split(" ").filter(Boolean);
  const candidates = bookmarks.filter((bookmark) => {
    if (!ownerBookmarkMatchesDestination(
      bookmark,
      filters.audienceID,
      filters.destination,
    )) return false;
    if (
      filters.cluster !== "all"
      && (
        filters.cluster === "unavailable"
          ? bookmark.cluster !== undefined
          : bookmark.cluster !== filters.cluster
      )
    ) return false;
    if (!ownerBookmarkMatchesReview(bookmark, filters.review)) return false;
    if (!sourceIncluded(
      bookmark,
      filters.includedSources,
      filters.excludedSources,
    )) return false;
    if (!queryTerms.length) return true;
    const sourcePresentation = ownerSourceLabel(ownerSourceID(bookmark));
    const searchable = normalizedText([
      bookmark.title,
      sourcePresentation,
      bookmark.cluster ?? "",
      bookmark.cluster ? humanizeCluster(bookmark.cluster) : "",
      bookmark.privateHumanSummary,
      bookmark.keywords.join(" "),
      bookmark.tags.join(" "),
    ].join(" "));
    return queryTerms.every((term) => searchable.includes(term));
  });

  return candidates.sort((left, right) => {
    switch (filters.sort) {
      case "newest":
        return (
          right.createdAt.localeCompare(left.createdAt)
          || compareBookmarkIdentity(left, right)
        );
      case "oldest":
        return (
          left.createdAt.localeCompare(right.createdAt)
          || compareBookmarkIdentity(left, right)
        );
      case "shuffled":
        return (
          stableHash(`${filters.shuffleSeed}\u0000${left.id}`)
          - stableHash(`${filters.shuffleSeed}\u0000${right.id}`)
          || compareBookmarkIdentity(left, right)
        );
    }
  });
}

function bookmarkNeedsReview(bookmark: Bookmark): boolean {
  return (
    ownerBookmarkMatchesReview(bookmark, "unclassified")
    || ownerBookmarkMatchesReview(bookmark, "summary")
    || ownerBookmarkMatchesReview(bookmark, "search_metadata")
    || ownerBookmarkMatchesReview(bookmark, "destination")
  );
}

export function ownerPreparationCounts(
  input: OwnerPreparationCountInput,
): OwnerPreparationCounts {
  const bookmarkIDs = new Set(input.bookmarks.map(({ id }) => id));
  const latestJobByBookmarkID = new Map<string, SummaryEnrichmentJob>();
  for (const job of input.enrichmentJobs) {
    latestJobByBookmarkID.set(job.bookmarkId, job);
  }
  let pending = 0;
  let failed = 0;
  let needsReview = 0;
  for (const bookmark of input.bookmarks) {
    const job = latestJobByBookmarkID.get(bookmark.id);
    if (job?.state === "failed" || bookmark.vectorStatus === "failed") {
      failed += 1;
    } else if (
      bookmark.vectorStatus === "pending"
      || job?.state === "pending"
      || job?.state === "queued"
    ) {
      pending += 1;
    }
    if (bookmarkNeedsReview(bookmark)) needsReview += 1;
  }
  return {
    total: input.bookmarks.length,
    shown: input.shownBookmarks.length,
    selected: [...input.selectedBookmarkIDs].filter((id) => (
      bookmarkIDs.has(id)
    )).length,
    unsaved: [...input.unsavedBookmarkIDs].filter((id) => (
      bookmarkIDs.has(id)
    )).length,
    pending,
    failed,
    needsReview,
  };
}
